import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "stores/authStore";
import { useSocket } from "src/socket/useSocket";

type UseChatListenersSocketParams = {
  discussionId?: string;
};

export const useChatListenersSocket = ({
  discussionId,
}: UseChatListenersSocketParams) => {
  const currentUserId = useAuthStore(state => state.currentUserId);
  const socket = useSocket();
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(
    new Map()
  );
  const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  useEffect(() => {
    setTypingUsers(new Map());
    typingTimers.current.forEach(timer => clearTimeout(timer));
    typingTimers.current.clear();
  }, [discussionId]);

  useEffect(() => {
    if (!socket || !currentUserId) return;

    const handleTyping = ({
      discussionId: socketDiscussionId,
      typing,
      userId,
      userName,
    }: {
      discussionId: string;
      typing: boolean;
      userId: string;
      userName?: string;
    }) => {
      if (socketDiscussionId !== discussionId || currentUserId === userId)
        return;

      const existingTimer = typingTimers.current.get(userId);
      if (existingTimer) clearTimeout(existingTimer);

      setTypingUsers(prev => {
        const next = new Map(prev);
        if (typing) {
          next.set(userId, userName ?? userId);
        } else {
          next.delete(userId);
        }
        return next;
      });

      if (typing) {
        const timer = setTimeout(() => {
          setTypingUsers(prev => {
            const next = new Map(prev);
            next.delete(userId);
            return next;
          });
          typingTimers.current.delete(userId);
        }, 5000);
        typingTimers.current.set(userId, timer);
      } else {
        typingTimers.current.delete(userId);
      }
    };

    socket.on("typing", handleTyping);

    return () => {
      socket.off("typing", handleTyping);
    };
  }, [socket, currentUserId, discussionId]);

  useEffect(() => {
    const timers = typingTimers.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const typingLabel =
    typingUsers.size > 0 ? [...typingUsers.values()].join(", ") : null;

  return { typingLabel };
};
